import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_1b05ad9b-00ea-4f44-a8ab-b9119f3f327f/artifacts/eb517133_Design%20ohne%20Titel%20%2811%29.png";

const Datenschutz = () => {
  const { dir, lang } = useLanguage();

  const backLabel = {
    ar: "العودة إلى الصفحة الرئيسية",
    de: "Zurück zur Startseite",
    en: "Back to home",
    fa: "بازگشت به صفحه اصلی",
  }[lang] || "Zurück zur Startseite";

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 100% 0%, rgba(31,107,122,0.10) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 0% 100%, rgba(232,163,101,0.08) 0%, transparent 60%)",
        }}
      />

      <header className="border-b border-[rgba(31,107,122,0.12)] bg-white/70 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="datenschutz-logo">
            <img
              src={LOGO_URL}
              alt="Kompass Heidelberg"
              className="object-contain"
              style={{
                mixBlendMode: "multiply",
                height: "clamp(64px, 8vw, 96px)",
                width: "auto",
              }}
            />
          </Link>
          <Link
            to="/"
            data-testid="datenschutz-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--kompass-primary)] hover:text-[color:var(--kompass-primary-dark)] transition-colors"
          >
            <ArrowLeft className={`h-4 w-4 ${dir === "rtl" ? "flip-x" : ""}`} strokeWidth={1.7} />
            {backLabel}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16" dir="ltr">
        <span className="eyebrow">Rechtliches</span>
        <h1 className="display display-1 mt-4 text-[color:var(--kompass-ink)]">
          Datenschutzerklärung
        </h1>

        <div className="mt-10 max-w-none text-[color:var(--kompass-ink-soft)] leading-relaxed space-y-8 text-[15px]">
          <section>
            <h2 className="text-xl font-semibold text-[color:var(--kompass-ink)] mb-4">
              1. Allgemeine Hinweise und Pflichtinformationen
            </h2>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-5 mb-2">
              Datenschutz
            </h3>
            <p>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen
              Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten
              vertraulich und entsprechend den gesetzlichen
              Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </p>
            <p className="mt-3">
              Wenn Sie diese Website benutzen, werden verschiedene
              personenbezogene Daten erhoben. Personenbezogene Daten sind Daten,
              mit denen Sie persönlich identifiziert werden können. Die
              vorliegende Datenschutzerklärung erläutert, welche Daten wir
              erheben und wofür wir sie nutzen. Sie erläutert auch, wie und zu
              welchem Zweck das geschieht.
            </p>
            <p className="mt-3">
              Wir weisen darauf hin, dass die Datenübertragung im Internet
              (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken
              aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff
              durch Dritte ist nicht möglich.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Hinweis zur verantwortlichen Stelle
            </h3>
            <p>
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser
              Website ist:
            </p>
            <p className="mt-3">
              Kompass — Service for Migrants
              <br />
              Eppelheimer Str. 21
              <br />
              69115 Heidelberg
              <br />
              Deutschland
            </p>
            <p className="mt-3">
              Telefon:{" "}
              <a
                href="tel:+491781647873"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                +49 178 164 78 73
              </a>
              <br />
              E-Mail:{" "}
              <a
                href="mailto:info@kompass-heidelberg.de"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                info@kompass-heidelberg.de
              </a>
            </p>
            <p className="mt-3">
              Verantwortliche Stelle ist die natürliche oder juristische Person,
              die allein oder gemeinsam mit anderen über die Zwecke und Mittel
              der Verarbeitung von personenbezogenen Daten (z. B. Namen,
              E-Mail-Adressen o. Ä.) entscheidet.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Speicherdauer
            </h3>
            <p>
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere
              Speicherdauer genannt wurde, verbleiben Ihre personenbezogenen
              Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt.
              Wenn Sie ein berechtigtes Löschbegehren geltend machen oder eine
              Einwilligung zur Datenverarbeitung widerrufen, werden Ihre Daten
              gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe
              für die Speicherung Ihrer personenbezogenen Daten haben (z. B.
              steuer- oder handelsrechtliche Aufbewahrungsfristen); im
              letztgenannten Fall erfolgt die Löschung nach Fortfall dieser
              Gründe.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Allgemeine Hinweise zu den Rechtsgrundlagen der Datenverarbeitung
              auf dieser Website
            </h3>
            <p>
              Sofern Sie in die Datenverarbeitung eingewilligt haben,
              verarbeiten wir Ihre personenbezogenen Daten auf Grundlage von
              Art. 6 Abs. 1 lit. a DSGVO bzw. Art. 9 Abs. 2 lit. a DSGVO,
              sofern besondere Datenkategorien nach Art. 9 Abs. 1 DSGVO
              verarbeitet werden. Im Falle einer ausdrücklichen Einwilligung
              in die Übertragung personenbezogener Daten in Drittstaaten
              erfolgt die Datenverarbeitung zudem auf Grundlage von Art. 49
              Abs. 1 lit. a DSGVO.
            </p>
            <p className="mt-3">
              Ist die Einwilligung in die Speicherung von Cookies oder in den
              Zugriff auf Informationen in Ihr Endgerät (z. B. via
              Device-Fingerprinting) erfolgt, erfolgt die Datenverarbeitung
              zusätzlich auf Grundlage von § 25 Abs. 1 TDDDG. Die Einwilligung
              ist jederzeit widerrufbar.
            </p>
            <p className="mt-3">
              Sind Ihre Daten zur Vertragserfüllung oder zur Durchführung
              vorvertraglicher Maßnahmen erforderlich, verarbeiten wir Ihre
              Daten auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO. Weiterhin
              verarbeiten wir Ihre Daten, sofern diese zur Erfüllung einer
              rechtlichen Verpflichtung erforderlich sind auf Grundlage von
              Art. 6 Abs. 1 lit. c DSGVO. Die Datenverarbeitung kann ferner
              auf Grundlage unseres berechtigten Interesses nach Art. 6 Abs. 1
              lit. f DSGVO erfolgen. Über die jeweils im Einzelfall vorliegenden
              Rechtsgrundlagen wird in den folgenden Absätzen dieser
              Datenschutzerklärung informiert.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Empfänger von personenbezogenen Daten
            </h3>
            <p>
              Im Rahmen unserer Geschäftstätigkeit arbeiten wir mit
              verschiedenen externen Stellen zusammen. Dabei ist teilweise auch
              eine Übermittlung von personenbezogenen Daten an diese externen
              Stellen erforderlich. Wir geben personenbezogene Daten nur dann
              an externe Stellen weiter, wenn dies im Rahmen einer
              Vertragserfüllung erforderlich ist, wenn wir gesetzlich hierzu
              verpflichtet sind (z. B. Weitergabe von Daten an Steuerbehörden),
              wenn wir ein berechtigtes Interesse nach Art. 6 Abs. 1 lit. f
              DSGVO an der Weitergabe haben oder wenn eine andere
              Rechtsgrundlage die Datenweitergabe erlaubt.
            </p>
            <p className="mt-3">
              Beim Einsatz von Auftragsverarbeitern geben wir die
              personenbezogenen Daten unserer Kunden nur auf Grundlage eines
              gültigen Vertrags über Auftragsverarbeitung weiter. Im Falle
              einer gemeinsamen Verarbeitung wird ein Vertrag über gemeinsame
              Verarbeitung (Art. 26 DSGVO) geschlossen.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Widerruf Ihrer Einwilligung zur Datenverarbeitung
            </h3>
            <p>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen
              Einwilligung möglich. Sie können eine bereits erteilte
              Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum
              Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf
              unberührt.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen
              sowie gegen Direktwerbung (Art. 21 DSGVO)
            </h3>
            <p className="uppercase text-[13px] leading-relaxed tracking-wide">
              Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e
              oder f DSGVO erfolgt, haben Sie jederzeit das Recht, aus Gründen,
              die sich aus Ihrer besonderen Situation ergeben, gegen die
              Verarbeitung Ihrer personenbezogenen Daten Widerspruch
              einzulegen; dies gilt auch für ein auf diese Bestimmungen
              gestütztes Profiling. Die jeweilige Rechtsgrundlage, auf denen
              eine Verarbeitung basiert, entnehmen Sie dieser
              Datenschutzerklärung. Wenn Sie Widerspruch einlegen, werden wir
              Ihre betroffenen personenbezogenen Daten nicht mehr verarbeiten,
              es sei denn, wir können zwingende schutzwürdige Gründe für die
              Verarbeitung nachweisen, die Ihre Interessen, Rechte und
              Freiheiten überwiegen oder die Verarbeitung dient der
              Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen
              (Widerspruch nach Art. 21 Abs. 1 DSGVO).
            </p>
            <p className="mt-3 uppercase text-[13px] leading-relaxed tracking-wide">
              Werden Ihre personenbezogenen Daten verarbeitet, um Direktwerbung
              zu betreiben, so haben Sie das Recht, jederzeit Widerspruch gegen
              die Verarbeitung Sie betreffender personenbezogener Daten zum
              Zweck derartiger Werbung einzulegen; dies gilt auch für das
              Profiling, soweit es mit solcher Direktwerbung in Verbindung
              steht. Wenn Sie widersprechen, werden Ihre personenbezogenen
              Daten anschließend nicht mehr zum Zwecke der Direktwerbung
              verwendet (Widerspruch nach Art. 21 Abs. 2 DSGVO).
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Beschwerderecht bei der zuständigen Aufsichtsbehörde
            </h3>
            <p>
              Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein
              Beschwerderecht bei einer Aufsichtsbehörde, insbesondere in dem
              Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes
              oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht
              besteht unbeschadet anderweitiger verwaltungsrechtlicher oder
              gerichtlicher Rechtsbehelfe.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Recht auf Datenübertragbarkeit
            </h3>
            <p>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer
              Einwilligung oder in Erfüllung eines Vertrags automatisiert
              verarbeiten, an sich oder an einen Dritten in einem gängigen,
              maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die
              direkte Übertragung der Daten an einen anderen Verantwortlichen
              verlangen, erfolgt dies nur, soweit es technisch machbar ist.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Auskunft, Berichtigung und Löschung
            </h3>
            <p>
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen
              jederzeit das Recht auf unentgeltliche Auskunft über Ihre
              gespeicherten personenbezogenen Daten, deren Herkunft und
              Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht
              auf Berichtigung oder Löschung dieser Daten. Hierzu sowie zu
              weiteren Fragen zum Thema personenbezogene Daten können Sie sich
              jederzeit unter der im Impressum angegebenen Adresse an uns
              wenden.
            </p>

            <h3 className="text-base font-semibold text-[color:var(--kompass-ink)] mt-6 mb-2">
              Recht auf Einschränkung der Verarbeitung
            </h3>
            <p>
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer
              personenbezogenen Daten zu verlangen. Hierzu können Sie sich
              jederzeit unter der im Impressum angegebenen Adresse an uns
              wenden. Das Recht auf Einschränkung der Verarbeitung besteht,
              wenn Sie die Richtigkeit Ihrer Daten bestreiten, die Verarbeitung
              unrechtmäßig ist, wir die Daten nicht mehr benötigen, Sie diese
              jedoch zur Ausübung oder Verteidigung von Rechtsansprüchen
              benötigen, oder wenn Sie Widerspruch nach Art. 21 Abs. 1 DSGVO
              eingelegt haben.
            </p>
          </section>

          <section className="pt-4 border-t border-[rgba(31,107,122,0.1)]">
            <p className="text-xs text-[color:var(--kompass-muted)]">
              Stand: {new Date().toLocaleDateString("de-DE", {
                year: "numeric",
                month: "long",
              })}
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Datenschutz;
